import type { Circuit, PlayerTrackPosition } from '@multirx/shared/types/track.js';
import { trackPositionGridKey } from '@multirx/shared/game/trackMovement.js';
import { trackMovementOccupancyKey } from '@multirx/shared/game/trackOccupancyKey.js';
import {
  computeLiveCarLaneOffset,
  LIVE_CAR_FOOTPRINT,
  LIVE_CAR_LATERAL_FOOTPRINT,
} from '../circuits/liveCarLaneOffset.js';
import { computeTokenCellLayout, type TokenCellLayout } from './tokenCellLayout.js';

/** Occupant d'une case physique (course live, pions 2D, replay). */
export interface CellOccupant {
  id: number;
  stamp: number;
  position: PlayerTrackPosition;
}

export type CellLayoutKind = 'token' | 'live3d';

/** Distance entre centres de deux voitures 3D sur une même case (fraction de case). */
export function pairSeparation(a: TokenCellLayout, b: TokenCellLayout): number {
  return Math.hypot(a.offsetX - b.offsetX, a.offsetY - b.offsetY);
}

/**
 * Tri stable : stamp croissant (premier arrivé / priorité), puis id.
 */
export function sortCellOccupants(occupants: readonly CellOccupant[]): CellOccupant[] {
  return [...occupants].sort((a, b) => a.stamp - b.stamp || a.id - b.id);
}

export type OccupancyKeyFn = (circuit: Circuit, position: PlayerTrackPosition) => string | null;

/**
 * Occupants d'une case (grille, ou cluster de virages si `keyOf` le dit).
 * `extra` remplace l'entrée du même id
 * (pion en cours d'animation déjà compté sur la case cible) et est
 * ajouté s'il manque — évite que deux voitures reçoivent stackIndex 0
 * et se superposent au centre.
 */
export function occupantsOnGridKey(
  circuit: Circuit,
  all: readonly CellOccupant[],
  gridKey: string | null,
  extra?: CellOccupant,
  keyOf: OccupancyKeyFn = trackPositionGridKey
): CellOccupant[] {
  if (!gridKey) {
    return extra ? [extra] : [];
  }
  const merged = extra ? [...all.filter((o) => o.id !== extra.id), extra] : [...all];
  return sortCellOccupants(merged.filter((o) => keyOf(circuit, o.position) === gridKey));
}

/** Occupants d’une même « case » de conflit (grille ou cluster de virages 90°). */
export function occupantsOnOccupancyKey(
  circuit: Circuit,
  all: readonly CellOccupant[],
  occupancyKey: string | null,
  extra?: CellOccupant
): CellOccupant[] {
  return occupantsOnGridKey(circuit, all, occupancyKey, extra, trackMovementOccupancyKey);
}

/**
 * Index de pile unique dans `occupants` (déjà filtrés sur la case).
 * Si le joueur est absent, il est ajouté en dernier plutôt que de
 * réutiliser le slot 0 du lead.
 */
export function occupantStack(
  occupants: readonly CellOccupant[],
  playerId: number
): { stackIndex: number; peerCount: number; leadId: number } {
  const sorted = sortCellOccupants(occupants);
  const list = sorted.some((o) => o.id === playerId)
    ? sorted
    : [
        ...sorted,
        {
          id: playerId,
          stamp: Number.POSITIVE_INFINITY,
          position: sorted[0]?.position ?? {
            mainPathIndex: 0,
            jokerBranchIndex: null,
            startLaneIndex: null,
          },
        },
      ];
  return {
    stackIndex: Math.max(
      0,
      list.findIndex((o) => o.id === playerId)
    ),
    peerCount: Math.max(1, list.length),
    leadId: list[0]?.id ?? playerId,
  };
}

/**
 * Layout de **tous** les occupants d'une case, même axe (forward partagé).
 * Garantit des offsets distincts : plus de superposition au même point.
 */
export function layoutsForOccupantsOnCell(
  occupants: readonly CellOccupant[],
  forward: { dx: number; dy: number },
  cellSize: number,
  kind: CellLayoutKind
): Map<number, TokenCellLayout> {
  const sorted = sortCellOccupants(occupants);
  const peerCount = sorted.length;
  const result = new Map<number, TokenCellLayout>();
  for (let stackIndex = 0; stackIndex < sorted.length; stackIndex++) {
    const layout =
      kind === 'live3d'
        ? computeLiveCarLaneOffset(forward, stackIndex, peerCount, cellSize)
        : computeTokenCellLayout(cellSize, forward, stackIndex, peerCount);
    result.set(sorted[stackIndex].id, layout);
  }
  return result;
}

/** True si deux layouts occupent (quasi) le même point — superposition. */
export function layoutsOverlap(a: TokenCellLayout, b: TokenCellLayout, epsilon = 1e-6): boolean {
  return pairSeparation(a, b) <= epsilon;
}

/**
 * True si les disques de diamètre `footprint * scale` se recouvrent.
 * Pour les voitures 3D, `footprint` = largeur latérale (`LIVE_CAR_LATERAL_FOOTPRINT`).
 * Deux emprises qui se touchent sans s'intersecter ne sont pas en chevauchement.
 */
export function footprintsOverlap(
  a: TokenCellLayout,
  b: TokenCellLayout,
  footprint: number
): boolean {
  const radiusA = (footprint * a.scale) / 2;
  const radiusB = (footprint * b.scale) / 2;
  return pairSeparation(a, b) < radiusA + radiusB - 1e-9;
}

function projectObb(
  cx: number,
  cy: number,
  hx: number,
  hy: number,
  px: number,
  py: number,
  halfL: number,
  halfW: number,
  ax: number,
  ay: number
): { min: number; max: number } {
  const center = cx * ax + cy * ay;
  const extent = Math.abs(hx * ax + hy * ay) * halfL + Math.abs(px * ax + py * ay) * halfW;
  return { min: center - extent, max: center + extent };
}

/**
 * Chevauchement des rectangles orientés (longueur × largeur) de deux voitures 3D.
 * `heading` est le cap visuel (tangente de l’arc en virage) — pas seulement l’axe
 * de décalage. Deux files cardinées sur un virage à 45° se recouvrent encore.
 */
export function orientedCarFootprintsOverlap(
  a: TokenCellLayout,
  b: TokenCellLayout,
  heading: { dx: number; dy: number },
  length: number = LIVE_CAR_FOOTPRINT,
  width: number = LIVE_CAR_LATERAL_FOOTPRINT
): boolean {
  const len = Math.hypot(heading.dx, heading.dy) || 1;
  const hx = heading.dx / len;
  const hy = heading.dy / len;
  const px = hy;
  const py = -hx;
  const axes: Array<[number, number]> = [
    [hx, hy],
    [px, py],
  ];
  for (const [ax, ay] of axes) {
    const projA = projectObb(
      a.offsetX,
      a.offsetY,
      hx,
      hy,
      px,
      py,
      (length * a.scale) / 2,
      (width * a.scale) / 2,
      ax,
      ay
    );
    const projB = projectObb(
      b.offsetX,
      b.offsetY,
      hx,
      hy,
      px,
      py,
      (length * b.scale) / 2,
      (width * b.scale) / 2,
      ax,
      ay
    );
    if (projA.max <= projB.min + 1e-9 || projB.max <= projA.min + 1e-9) {
      return false;
    }
  }
  return true;
}
